import React from 'react';
import { PlayButton } from 'components/UI/PlayButton';
import { LogService } from '@landr/core';
import { Heading } from '../../LANDR/Heading';
import { Grid, GridCol, GridRow } from '../../UI/Grid';
import { log } from '../../../log';
import { EventId } from '../../../enum/eventId';
import { withAppContext } from '../../../context/AppContext';
import {
    ABPlayersSources,
    Item,
    MasteringDemosProps,
    MasteringDemosState,
    Versions,
} from './types';
import { ABPlayers } from './ABPlayers';
import { StyleImage } from './StyleImage';
import { BtnVersion } from './BtnVersion';
import { Style } from './Style';
import { backgroundResize, componentDidMountAnimation, moveToggle } from './Animations';
import './MasteringDemos.scss';

const createABPlayersSources = (items: Item[]): ABPlayersSources[] =>
    items.reduce(
        (accumulator, style) => [
            ...accumulator,
            {
                [Versions.original]: style.mastering_demos_original.url,
                [Versions.mastered]: style.mastering_demos_mastered.url,
            },
        ],
        [],
    );

class MasteringDemosComponent extends React.PureComponent<
    MasteringDemosProps,
    MasteringDemosState
> {
    state = {
        selectedStyle: 0,
        isPlaying: false,
        selectedVersion: Versions.original,
    };

    SelectedBackgroundBtn: React.RefObject<any>;
    BtnOriginal: React.RefObject<any>;
    BtnMastered: React.RefObject<any>;
    originalBtnWidth: number;
    masterBtnWidth: number;
    log: LogService;

    componentDidMount() {
        this.log = log.getInstance('MasteringDemos');

        componentDidMountAnimation();

        window.addEventListener('resize', () => {
            backgroundResize(this.BtnOriginal, this.BtnMastered, this.SelectedBackgroundBtn);
        });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', () => {
            backgroundResize(this.BtnOriginal, this.BtnMastered, this.SelectedBackgroundBtn);
        });
    }

    componentDidCatch(error: Error) {
        this.log.error('Mastering Demos slice failed', EventId.AbPlayerFailed, error);
    }

    setSelectedStyle(i: number) {
        this.setState({
            selectedStyle: i,
            isPlaying: false,
            selectedVersion: Versions.original,
        });
        this.animateToggle(Versions.original);
        this.props.context.segment.trackCarouselSlideChanged(this.props.sliceContext);
    }

    setSelectedVersion(version: Versions) {
        this.setState({
            selectedVersion: version,
        });

        this.originalBtnWidth = this.BtnOriginal.current.offsetWidth;
        this.masterBtnWidth = this.BtnMastered.current.offsetWidth;
        this.SelectedBackgroundBtn.current.style.width = `${this.originalBtnWidth}px`;
        if (!this.state.isPlaying) {
            this.play();
        }

        this.animateToggle(version);

        this.props.context.segment.trackAudioPlayerToggled(
            {
                'L - Asset Version': version,
            },
            this.props.sliceContext,
        );
    }

    animateToggle(version: any) {
        if (version === Versions.original) {
            this.SelectedBackgroundBtn.current.style.width = `${this.originalBtnWidth}px`;
            moveToggle(this.SelectedBackgroundBtn.current, 0);
        } else {
            this.SelectedBackgroundBtn.current.style.width = `${this.masterBtnWidth}px`;
            moveToggle(this.SelectedBackgroundBtn.current, this.originalBtnWidth);
        }
    }

    switchPlayerState() {
        this.state.isPlaying === false ? this.play() : this.stop();
    }

    play() {
        this.setState({
            isPlaying: true,
        });
    }

    stop() {
        this.setState({
            isPlaying: false,
        });
    }
    render() {
        const {
            slice: {
                primary: {
                    mastering_demos_mastered_button_copy: masteredButtonCopy,
                    mastering_demos_original_button_copy: originalButtonCopy,
                    mastering_demos_title: { raw },
                },
                items: styles,
            },
        } = this.props;

        this.SelectedBackgroundBtn = React.createRef();
        this.BtnOriginal = React.createRef();
        this.BtnMastered = React.createRef();
        return (
            <div className="MasteringDemos-fullscreen">
                <Grid className="MasteringDemos-container">
                    <ABPlayers
                        sources={createABPlayersSources(styles)}
                        version={this.state.selectedVersion}
                        current={this.state.selectedStyle}
                        isPlaying={this.state.isPlaying}
                        currentStyle={styles}
                        segmentService={this.props.context.segment}
                        sliceContext={this.props.sliceContext}
                    />
                    <GridRow
                        horizontalAlign={'h-start'}
                        verticalAlign={'v-center'}
                        className="FeaturedArtists-content"
                    >
                        <GridCol colSize={`12`} offsetSize={`0`} className="Wrapper-info">
                            {raw && raw[0] && (
                                <Heading
                                    element={raw[0].type}
                                    size="xl"
                                    className="MasteringDemos-title"
                                >
                                    {raw[0].text}
                                </Heading>
                            )}
                            {styles.map((style, i) => (
                                <div key={i}>
                                    <Style
                                        key={i}
                                        style={style}
                                        setSelectedStyle={() => this.setSelectedStyle(i)}
                                        isSelectedStyle={i === this.state.selectedStyle}
                                    />
                                </div>
                            ))}
                            <GridCol
                                colSize={`6`}
                                offsetSize={`0`}
                                mobileColSize={'12'}
                                className="Wrapper-info-bottom"
                            >
                                <div className="Wrapper-abcontrol">
                                    <canvas id="Progress-bar" width="100px" height="100px" />
                                    <div className="Progress-bg" />
                                    <PlayButton
                                        isPlaying={this.state.isPlaying}
                                        audioAction={() => this.switchPlayerState()}
                                        className="MasteringDemos-playButton"
                                    />
                                </div>
                            </GridCol>
                            <GridCol
                                colSize={`6`}
                                offsetSize={`0`}
                                mobileColSize={'12'}
                                className="Block-version"
                            >
                                <BtnVersion
                                    changeVersionToOriginal={() =>
                                        this.setSelectedVersion(Versions.original)
                                    }
                                    changeVersionToMastered={() =>
                                        this.setSelectedVersion(Versions.mastered)
                                    }
                                    selectedVersion={this.state.selectedVersion}
                                    masteredButtonCopy={masteredButtonCopy}
                                    originalButtonCopy={originalButtonCopy}
                                    SelectedBackgroundBtn={this.SelectedBackgroundBtn}
                                    BtnOriginal={this.BtnOriginal}
                                    BtnMastered={this.BtnMastered}
                                />
                            </GridCol>
                        </GridCol>
                    </GridRow>
                </Grid>
                {styles.map((style, i) => (
                    <StyleImage
                        key={i}
                        style={style}
                        isSelectedStyle={i === this.state.selectedStyle}
                    />
                ))}
            </div>
        );
    }
}

const MasteringDemos = withAppContext(MasteringDemosComponent);

export { MasteringDemos, MasteringDemosComponent };
